import * as React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { carrierCardStyles } from './styles';
import { Edit, Delete } from '@mui/icons-material';
import { NotesIcon, CompanyIcon } from 'components/Shared/Icons/tooltip_icons';
import { Carrier } from 'types/entities/types';
import { useState } from 'react';
import axiosService from 'utils/axios';
import AddOrEditCarrier from './AddOrEditCarrier';
import CompanyCard from '../companies/CompanyCard';
import CardTitle from 'components/Shared/CardsView/CardTitle';
import ModalErrorPopup from '../../../components/Modal/ModalErrorPopup';
import { ErrorResponse } from '../../../types/general/types';

interface Props {
  option: Carrier;
  showActionButtons: boolean;
  onCarrierDelete?: () => void;
  onCarrierUpdate?: () => void;
}

const CarrierCard = (props: Props) => {
  const { option: carrier, showActionButtons = true, onCarrierDelete, onCarrierUpdate } = props;

  const [editCarrierModalOpen, setEditCarrierModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<ErrorResponse | null>();

  const deleteCarrier = () => {
    axiosService
      .delete(`/entities/carrier/${carrier.id}/`)
      .then(() => {
        if (onCarrierDelete) {
          onCarrierDelete();
        }
      })
      .catch(error => setDeleteError(error))
      .finally(() => setDeleteDialogOpen(false));
  };

  const onCarrierEdit = () => {
    if (onCarrierUpdate) {
      onCarrierUpdate();
    }
    setEditCarrierModalOpen(false);
  };

  return (
    <>
      <Card elevation={1} sx={carrierCardStyles.optionCard}>
        <CardHeader
          action={
            showActionButtons ? (
              <>
                <Tooltip title="Edit">
                  <IconButton
                    sx={carrierCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setEditCarrierModalOpen(true);
                    }}
                  >
                    <Edit />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    sx={carrierCardStyles.cardAction}
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();
                      setDeleteDialogOpen(true);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </>
            ) : null
          }
          title={<CardTitle title={carrier.company.name} tooltipText={carrier.id} />}
          subheader="Carrier"
          sx={carrierCardStyles.cardHeader}
        />
        <CardContent>
          <Box sx={carrierCardStyles.contactBox}>
            <CompanyIcon />
            <Tooltip
              title={<CompanyCard option={carrier.company} showActionButtons={false} />}
              arrow
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: 'transparent',
                    maxWidth: 'none',
                  },
                },
              }}
            >
              <Typography color="text" sx={carrierCardStyles.contactValue}>
                {carrier.company.name || '-'}
              </Typography>
            </Tooltip>
          </Box>
          <Box sx={carrierCardStyles.contactBox}>
            <NotesIcon />
            <Typography color="text" sx={carrierCardStyles.contactValue}>
              {carrier.notes || '-'}
            </Typography>
          </Box>
        </CardContent>
      </Card>
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle id="alert-dialog-title">Delete carrier</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete carrier <b>{carrier.company.name}</b>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={deleteCarrier} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      <AddOrEditCarrier
        carrier={carrier}
        onSave={onCarrierEdit}
        modalTitle="Edit carrier"
        isModalOpen={editCarrierModalOpen}
        setModalOpen={setEditCarrierModalOpen}
      />
      {/* @ts-ignore:next-line*/}
      <ModalErrorPopup
        title={deleteError?.statusText}
        message={deleteError?.message}
        onClose={() => setDeleteError(null)}
      />
    </>
  );
};

export default CarrierCard;
